import { formatDate } from '@/lib/format';
import type { DeskFunctionSheet } from '@/types/desk';

function asList(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function itemLabel(item: unknown): string {
  if (!item || typeof item !== 'object') return String(item ?? '');
  const o = item as Record<string, unknown>;
  return String(
    o.description ||
      o.Description ||
      o.name ||
      o.Name ||
      o.title ||
      JSON.stringify(item),
  );
}

type Props = {
  sheet: DeskFunctionSheet;
  eventName?: string;
  readOnly?: boolean;
};

export function FunctionSheetView({ sheet, eventName, readOnly }: Props) {
  const menus = asList(sheet.menus);
  const catering = asList(sheet.catering);
  const av = asList(sheet.av);
  const lines = sheet.line_items || [];
  const charges = sheet.charges || [];

  return (
    <article
      className="function-sheet rounded-xl border border-[var(--border)] bg-white p-5 print:border-0 print:p-0"
      data-cimso-readonly={readOnly ? 'true' : 'false'}
    >
      <header className="border-b border-[var(--border)] pb-3 print:border-black">
        <p className="text-xs uppercase tracking-wide text-[var(--muted)] print:text-black">
          Function sheet / BEO
          {readOnly ? ' · Cimso (read-only)' : ''}
        </p>
        <h2 className="font-display text-xl text-dome-forest print:text-black">
          {sheet.title || eventName || 'Function sheet'}
        </h2>
        <p className="mt-1 text-sm text-[var(--muted)] print:text-black">
          {formatDate(sheet.starts_at)}
          {sheet.ends_at ? ` → ${formatDate(sheet.ends_at)}` : ''}
          {sheet.status ? ` · ${sheet.status}` : ''}
        </p>
      </header>

      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-[var(--muted)] print:font-semibold">Venue</dt>
          <dd>{sheet.venue_name || '—'}</dd>
        </div>
        <div>
          <dt className="text-[var(--muted)] print:font-semibold">Room</dt>
          <dd>{sheet.room_name || '—'}</dd>
        </div>
        <div>
          <dt className="text-[var(--muted)] print:font-semibold">Setup</dt>
          <dd>{sheet.setup || '—'}</dd>
        </div>
        <div>
          <dt className="text-[var(--muted)] print:font-semibold">Layout</dt>
          <dd>{sheet.layout || '—'}</dd>
        </div>
        <div>
          <dt className="text-[var(--muted)] print:font-semibold">Pax</dt>
          <dd>{sheet.pax ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-[var(--muted)] print:font-semibold">Cimso IDs</dt>
          <dd className="font-mono text-xs">
            booking {sheet.cimso_booking_id ?? '—'}
            {sheet.cimso_function_id != null
              ? ` · function ${sheet.cimso_function_id}`
              : ''}
          </dd>
        </div>
      </dl>

      {sheet.notes ? (
        <section className="mt-4">
          <h3 className="font-display text-base text-dome-forest print:text-black">
            Notes
          </h3>
          <p className="mt-1 whitespace-pre-wrap text-sm">{sheet.notes}</p>
        </section>
      ) : null}

      {menus.length > 0 ? (
        <section className="mt-4">
          <h3 className="font-display text-base text-dome-forest print:text-black">
            Menus
          </h3>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
            {menus.map((m, i) => (
              <li key={i}>{itemLabel(m)}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {catering.length > 0 ? (
        <section className="mt-4">
          <h3 className="font-display text-base text-dome-forest print:text-black">
            Catering
          </h3>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
            {catering.map((m, i) => (
              <li key={i}>{itemLabel(m)}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {av.length > 0 ? (
        <section className="mt-4">
          <h3 className="font-display text-base text-dome-forest print:text-black">
            AV / equipment
          </h3>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-sm">
            {av.map((m, i) => (
              <li key={i}>{itemLabel(m)}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {lines.length > 0 ? (
        <section className="mt-4">
          <h3 className="font-display text-base text-dome-forest print:text-black">
            Line items
          </h3>
          <table className="mt-2 w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-[var(--muted)]">
                <th className="py-1 pr-2 font-medium">Description</th>
                <th className="py-1 pr-2 font-medium">Cat.</th>
                <th className="py-1 pr-2 font-medium">Qty</th>
                <th className="py-1 font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => (
                <tr key={i} className="border-b border-[var(--border)]">
                  <td className="py-1.5 pr-2">{line.description}</td>
                  <td className="py-1.5 pr-2">{line.category}</td>
                  <td className="py-1.5 pr-2">{line.quantity ?? '—'}</td>
                  <td className="py-1.5">{line.amount ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {charges.length > 0 ? (
        <section className="mt-4">
          <h3 className="font-display text-base text-dome-forest print:text-black">
            Charges
          </h3>
          <ul className="mt-1 space-y-1 text-sm">
            {charges.map((c, i) => (
              <li key={i} className="flex justify-between gap-3">
                <span>
                  {c.description}
                  {c.quantity != null ? ` × ${c.quantity}` : ''}
                </span>
                <span>{c.amount ?? '—'}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {sheet.cimso_synced_at ? (
        <p className="mt-4 text-xs text-[var(--muted)] print:hidden">
          Synced from Cimso {formatDate(sheet.cimso_synced_at)}
        </p>
      ) : null}
    </article>
  );
}
